import { BRAND_SECONDARY } from "../../../utils/Styles";
import React from "react";
import {
  Stack,
  Checkbox,
  DefaultButton,
  Callout,
  DirectionalHint,
} from "office-ui-fabric-react";
import { IWaterCooledChillerReading, IReadingStatus } from "../../../vm";
import {
  ODD_SHIFT,
  UPS_STATUS,
  READ_AT_TIME_FORMAT,
  childrenGap8,
  EVEN_SHIFT,
} from "../../common/Constants";
import moment from "moment";
import Icon from "@mdi/react";
import { mdiDotsVertical } from "@mdi/js";
import ManageWccDialog from "./ManageWccDialog";
import { ToastContext } from "../../common/ToastProvider";
import { ConfirmDialogContext } from "../../common/ConfirmDialogProvider";
import { updateWccReadingStatus } from "../../../services/infrastructure/AccService";
import {
  isInThreshold,
  isValueLessThanThreshold,
} from "../../../services/ThresholdService";
import ManageAllRejectDialog from "../../common/ManageAllRejectDialog";
import ShowThresholdIcon from "../../common/ShowThresholdIcon";

export interface WccTableProps {
  accReading: IWaterCooledChillerReading[];
  threshold: any;
}

const WccTable: React.FC<WccTableProps> = ({ accReading, threshold }) => {
  //state
  const [state, setState] = React.useState({
    isLoading: false,
    manageWccDialog: {
      isOpen: false,
      index: undefined,
      data: undefined,
    },
    wccReading: [],
    isAllChecked: false,
    selectedItems: [],
    manageRejectDialog: {
      isOpen: false,
      ids: [],
      remarks: "",
    },
    manageActionsCallout: {
      isOpen: false,
      element: undefined,
      data: undefined,
    },
  } as {
    isLoading: boolean;
    manageWccDialog: {
      isOpen: boolean;
      index?: number;
      data?: IWaterCooledChillerReading;
    };
    wccReading: IWaterCooledChillerReading[];
    isAllChecked: boolean;
    selectedItems: string[];
    manageRejectDialog: {
      isOpen: boolean;
      ids: string[];
      remarks?: string;
    };
    manageActionsCallout: {
      isOpen: boolean;
      element: any;
      data?: IWaterCooledChillerReading;
    };
  });

  React.useEffect(() => {
    setState({ ...state, wccReading: [...accReading] });
  }, [accReading]);

  //hooks
  const { showToast } = React.useContext(ToastContext);
  const { showConfirmDialog } = React.useContext(ConfirmDialogContext);

  const handleAcceptOrRejectSelected = (isAccept: boolean) => {
    if (isAccept) {
      showConfirmDialog(
        "Are You Sure",
        `Do you want to ${
          isAccept ? "Accept" : "Reject"
        } selected water cooled chiller reading`,
        async () => {
          let selectedItems = [...state.selectedItems];
          let wccReading = [...state.wccReading];
          setState({ ...state, isLoading: true });
          let obj: IReadingStatus = {
            ids: selectedItems,
            status: isAccept ? "approved" : "rejected",
          };
          let result = await updateWccReadingStatus(obj);
          if (result?.success) {
            selectedItems.forEach((item) => {
              let index = wccReading.findIndex((ele) => ele._id === item);
              if (index !== -1) {
                wccReading[index].status = isAccept ? "approved" : "rejected";
              }
            });
            showToast(
              `Water Cooled Chiller Reading ${
                isAccept ? "approved" : "rejected"
              } successfully`,
              "success"
            );
            setState({
              ...state,
              wccReading,
              selectedItems: [],
              isLoading: false,
            });
          } else {
            showToast(
              result?.message ||
                `An error occurred while ${
                  isAccept ? "approving" : "rejecting"
                } a water cooled chiller reading`,
              "error"
            );
            setState({
              ...state,
              isLoading: false,
            });
          }
        }
      );
    } else {
      let selectedItems = [...state.selectedItems];
      handleRejectAllOpenDialog(selectedItems);
    }
  };

  const onSelectAll = (isAllChecked: boolean) => {
    let selectedItems: string[] = [];
    if (isAllChecked) {
      state.wccReading.forEach((ele: any) => {
        if (ele.status === "submitted") {
          selectedItems.push(ele._id as string);
        }
      });
      setState({ ...state, isAllChecked: true, selectedItems });
    } else {
      setState({ ...state, isAllChecked: false, selectedItems });
    }
  };

  const onIndividualSelect = (id: string) => {
    let selectedItems = [...state.selectedItems];
    let index = selectedItems.findIndex((e) => e === id);
    if (index === -1) {
      selectedItems.push(id);
    } else {
      selectedItems.splice(index, 1);
    }
    let isAllChecked = true;
    state.wccReading.forEach((ele: any) => {
      if (
        ele.status === "submitted" &&
        selectedItems.includes(ele._id as string) === false
      ) {
        isAllChecked = false;
      }
    });
    setState({ ...state, selectedItems, isAllChecked });
  };

  const handleAcceptOrReject = (id: string, isAccept: boolean) => {
    showConfirmDialog(
      "Are You Sure",
      `Do you want to ${
        isAccept ? "Accept" : "Reject"
      } the water cooled chiller reading`,
      async () => {
        setState({
          ...state,
          isLoading: true,
          manageActionsCallout: {
            isOpen: false,
            element: undefined,
            data: undefined,
          },
        });
        let obj: IReadingStatus = {
          ids: [id],
          status: isAccept ? "approved" : "rejected",
        };
        let result = await updateWccReadingStatus(obj);
        if (result.success) {
          let wccReading = [...state.wccReading];
          let index = wccReading.findIndex((ele) => ele._id === id);
          if (index !== -1) {
            wccReading[index].status = isAccept ? "approved" : "rejected";
          }
          showToast(
            `Air-Cooled-Chiller Reading ${
              isAccept ? "approved" : "rejected"
            } successfully`,
            "success"
          );
          let selectedItems = [...state.selectedItems];
          if (state.isAllChecked) {
            let indx = selectedItems.findIndex((e) => e === id);
            selectedItems.splice(indx, 1);
          }
          setState({
            ...state,
            wccReading,
            selectedItems,
            isLoading: false,
            manageActionsCallout: {
              isOpen: false,
              element: undefined,
              data: undefined,
            },
          });
        } else {
          showToast(
            result?.message ||
              `An error occurred while ${
                isAccept ? "approving" : "rejecting"
              } a water cooled chiller reading`,
            "error"
          );
          setState({
            ...state,
            isLoading: false,
            manageActionsCallout: {
              isOpen: false,
              element: undefined,
              data: undefined,
            },
          });
        }
      }
    );
  };

  const handleDialogOpen = (isOpen: boolean, id?: string) => {
    let wccReading = undefined;
    let index = undefined;
    if (id !== undefined) {
      index = state.wccReading.findIndex((ele) => ele._id === id);
      wccReading = state.wccReading[index];
    }
    setState({
      ...state,
      manageWccDialog: {
        isOpen: isOpen,
        index: index,
        data: wccReading,
      },
    });
  };

  const handleDialogClose = (data: IWaterCooledChillerReading) => {
    setState({ ...state, isLoading: true });
    let wccReading = [...state.wccReading];
    if (data) {
      if (state.manageWccDialog.index !== undefined) {
        wccReading[state.manageWccDialog.index] = data;
      }
    }
    setState({
      ...state,
      isLoading: false,
      manageWccDialog: {
        isOpen: false,
        index: undefined,
        data: undefined,
      },
      wccReading,
    });
  };

  const handleRejectAllOpenDialog = (ids: string[]) => {
    setState({
      ...state,
      manageRejectDialog: {
        isOpen: true,
        ids: ids || [],
        remarks: "",
      },
    });
  };
  const handleRejectOpenDialog = (id: string, remarks?: string) => {
    setState({
      ...state,
      manageRejectDialog: {
        isOpen: true,
        ids: [id],
        remarks: remarks || "",
      },
    });
  };

  const handleRejectCloseDialog = async (
    isSuccess: boolean,
    remarks?: string
  ) => {
    let wccReading = [...state.wccReading];
    let selectedItems: string[] = state.manageRejectDialog.ids;
    if (isSuccess) {
      setState({
        ...state,
        isLoading: true,
      });
      let obj: IReadingStatus = {
        ids: selectedItems,
        status: "rejected",
        remarks: remarks || "",
      };
      let result = await updateWccReadingStatus(obj);
      if (result?.success) {
        selectedItems.forEach((item) => {
          let index = wccReading.findIndex((ele) => ele._id === item);
          if (index !== -1) {
            wccReading[index].status = "rejected";
            wccReading[index].remarks = remarks || "";
          }
        });
        showToast(
          `Water Cooled Chiller Reading rejected successfully`,
          "success"
        );
        setState({
          ...state,
          wccReading,
          selectedItems: [],
          manageRejectDialog: {
            isOpen: false,
            ids: [],
            remarks: undefined,
          },
          isLoading: false,
        });
      } else {
        showToast(
          result?.message ||
            `An error occurred while rejecting a water cooled chiller reading`,
          "error"
        );
        setState({
          ...state,
          manageRejectDialog: {
            isOpen: false,
            ids: [],
            remarks: undefined,
          },
          isLoading: false,
        });
      }
    } else {
      setState({
        ...state,
        manageRejectDialog: {
          isOpen: false,
          ids: [],
          remarks: undefined,
        },
        wccReading,
      });
    }
  };

  const handleLoading = () => {
    setState({
      ...state,
      isLoading: !state.isLoading,
    });
  };

  const handleCallOutOpenClose = (
    isOpen: boolean,
    ele?: React.MouseEvent<HTMLSpanElement>,
    data?: IWaterCooledChillerReading
  ) => {
    setState({
      ...state,
      manageActionsCallout: {
        isOpen,
        element: isOpen ? ele?.currentTarget : undefined,
        data,
      },
    });
  };

  return (
    <React.Fragment>
      <Stack horizontal horizontalAlign="end">
        <DefaultButton
          text="Accept"
          disabled={state.selectedItems.length === 0}
          onClick={() => handleAcceptOrRejectSelected(true)}
          className="custom-secondary-btn"
        />
        &nbsp;&nbsp;
        <DefaultButton
          text="Reject"
          disabled={state.selectedItems.length === 0}
          onClick={() => handleAcceptOrRejectSelected(false)}
          className="custom-secondary-btn"
        />
      </Stack>
      <br />
      <Stack>
        <div className="office-table bg-white">
          <table className="ms-Table bordered-table w-100">
            <thead className="html-table-thead">
              <tr>
                <th rowSpan={3}>
                  <Checkbox
                    checked={state.isAllChecked}
                    onChange={(eve, checked) => onSelectAll(checked as boolean)}
                  />
                </th>
                <th rowSpan={3}>Time (Hrs)</th>
                <th rowSpan={2}>System</th>
                <th colSpan={11}>EVAPORATOR</th>
                <th colSpan={10}>CONDENSER</th>
                <th colSpan={16}>COMPRESSOR</th>
                <th colSpan={28}>MOTOR</th>
                <th colSpan={26}>PURGE</th>
                <th rowSpan={3}>Shift Tech Sign</th>
                <th rowSpan={3}>Status</th>
                <th rowSpan={3}>Remarks</th>
                <th rowSpan={3}>Read At</th>
                <th rowSpan={3}>Actions</th>
              </tr>
              <tr>
                <th rowSpan={2}>CHW Inlet Pressure</th>
                <th rowSpan={2}>CHW Outlet Pressure</th>
                <th rowSpan={2}>Active CHW Setpoint </th>
                <th rowSpan={2}>CHW Inlet/Entering Temp</th>
                <th rowSpan={2}>CHW Outlet/Leaving Temp</th>
                <th colSpan={2}>Refrigerent Saturated Temp</th>
                <th colSpan={2}>Refrigerant Pressure</th>
                <th colSpan={2}>Evap. Approch</th>
                <th rowSpan={2}>Condenser Inlet Pressure</th>
                <th rowSpan={2}>Condenser Outlet Pressure</th>
                <th rowSpan={2}>Condenser Inlet/Entering Temp</th>
                <th rowSpan={2}>Condenser Outlet/Leaving Temp</th>
                <th colSpan={2}>Refrigerent Saturated Temp</th>
                <th colSpan={2}>Refrigerant Pressure</th>
                <th colSpan={2}>Condenser Approach</th>
                <th colSpan={2}>Starts</th>
                <th colSpan={2}>Running Time</th>
                <th colSpan={2}>Oil Tank Pressure</th>
                <th colSpan={2}>Oil Discharg Pressure</th>
                <th colSpan={2}>Oil Diff. Oil Pressure (KPad)</th>
                <th colSpan={2}>Oil Tank Temp</th>
                <th colSpan={2}>IGV Position %</th>
                <th colSpan={2}>IGV Steps</th>
                <th colSpan={2}>% RLA L1</th>
                <th colSpan={2}>% RLA L2</th>
                <th colSpan={2}>% RLA L3</th>
                <th colSpan={2}>Comp. Line Current L1</th>
                <th colSpan={2}>Comp. Line Current L2</th>
                <th colSpan={2}>Comp. Line Current L3</th>
                <th colSpan={2}>Comp. Line Volts AB</th>
                <th colSpan={2}>Comp. Line Volts BC</th>
                <th colSpan={2}>Comp. Line Volts AC</th>
                <th colSpan={2}>Compressor Kilowatts</th>
                <th colSpan={2}>Compressor Power Factor</th>
                <th colSpan={2}>Comp. Winding Temp W1</th>
                <th colSpan={2}>Comp. Winding Temp W2</th>
                <th colSpan={2}>Comp. Winding Temp W3</th>
                <th colSpan={2}>Purge Mode</th>
                <th colSpan={2}>Time Until Next Purge</th>
                <th colSpan={2}>Daily Pump Out-24 Hrs</th>
                <th colSpan={2}>Avg Pump Out-7 Days</th>
                <th colSpan={2}>Daily Pump Out Limit</th>
                <th colSpan={2}>Chiller on 7 Days</th>
                <th colSpan={2}>Pumpout Chiller On 7 Days</th>
                <th colSpan={2}>Pumpout Chiller Off 7 Days</th>
                <th colSpan={2}>Pump Out Life</th>
                <th colSpan={2}>Purge ref Comp. Suction Temp</th>
                <th colSpan={2}>Purge Liquid Temp</th>
                <th colSpan={2}>Carbon Tank Temp</th>
                <th colSpan={2}>Oil Tank Level</th>
              </tr>
              <tr>
                <th>CKT 1</th>
                <th>CKT 2</th>
                <th>CKT 1</th>
                <th>CKT 2</th>
                <th>CKT 1</th>
                <th>CKT 2</th>
                <th>CKT 1</th>
                <th>CKT 2</th>
                <th>CKT 1</th>
                <th>CKT 2</th>
                <th>CKT 1</th>
                <th>CKT 2</th>
                <th>CKT 1</th>
                <th>CKT 2</th>
                <th>CKT 1</th>
                <th>CKT 2</th>
                <th>CKT 1</th>
                <th>CKT 2</th>
                <th>CKT 1</th>
                <th>CKT 2</th>
                <th>CKT 1</th>
                <th>CKT 2</th>
                <th>CKT 1</th>
                <th>CKT 2</th>
                <th>CKT 1</th>
                <th>CKT 2</th>
                <th>CKT 1</th>
                <th>CKT 2</th>
                <th>CKT 1</th>
                <th>CKT 2</th>
                <th>CKT 1</th>
                <th>CKT 2</th>
                <th>CKT 1</th>
                <th>CKT 2</th>
                <th>CKT 1</th>
                <th>CKT 2</th>
                <th>CKT 1</th>
                <th>CKT 2</th>
                <th>CKT 1</th>
                <th>CKT 2</th>
                <th>CKT 1</th>
                <th>CKT 2</th>
                <th>CKT 1</th>
                <th>CKT 2</th>
                <th>CKT 1</th>
                <th>CKT 2</th>
                <th>CKT 1</th>
                <th>CKT 2</th>
                <th>CKT 1</th>
                <th>CKT 2</th>
                <th>CKT 1</th>
                <th>CKT 2</th>
                <th>CKT 1</th>
                <th>CKT 2</th>
                <th>CKT 1</th>
                <th>CKT 2</th>
                <th>CKT 1</th>
                <th>CKT 2</th>
                <th>CKT 1</th>
                <th>CKT 2</th>
                <th>CKT 1</th>
                <th>CKT 2</th>
                <th>CKT 1</th>
                <th>CKT 2</th>
                <th>CKT 1</th>
                <th>CKT 2</th>
                <th>CKT 1</th>
                <th>CKT 2</th>
                <th>CKT 1</th>
                <th>CKT 2</th>
                <th>CKT 1</th>
                <th>CKT 2</th>
                <th>CKT 1</th>
                <th>CKT 2</th>
                <th>CKT 1</th>
                <th>CKT 2</th>
                <th>CKT 1</th>
                <th>CKT 2</th>
                <th>CKT 1</th>
                <th>CKT 2</th>
                <th>CKT 1</th>
                <th>CKT 2</th>
              </tr>
            </thead>
            <tbody>
              {EVEN_SHIFT.map((ele, index) => {
                let item = state.wccReading.find((e) => e.shiftTime === ele);
                return (
                  <tr key={index}>
                    <th>
                      <Checkbox
                        disabled={!item || item.status !== "submitted"}
                        checked={state.selectedItems.includes(
                          item?._id as string
                        )}
                        onChange={() => onIndividualSelect(item?._id as string)}
                        className="ml-12"
                      />
                    </th>
                    <th>{ele}</th>
                    <td>
                      {item?.isSystemOn !== undefined
                        ? item?.isSystemOn === true
                          ? "On"
                          : "Off"
                        : ""}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.evaporator?.chwInletPressure || "",
                          "evaporator.chwInletPressure",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.evaporator?.chwInletPressure || ""}
                      {isInThreshold(
                        item?.evaporator?.chwInletPressure || "",
                        "evaporator.chwInletPressure",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.evaporator?.chwInletPressure || "",
                              "evaporator.chwInletPressure",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.evaporator?.chwOutletPressure || "",
                          "evaporator.chwOutletPressure",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.evaporator?.chwOutletPressure || ""}
                      {isInThreshold(
                        item?.evaporator?.chwOutletPressure || "",
                        "evaporator.chwOutletPressure",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.evaporator?.chwOutletPressure || "",
                              "evaporator.chwOutletPressure",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.evaporator?.activeChwSetPoint || "",
                          "evaporator.activeChwSetPoint",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.evaporator?.activeChwSetPoint || ""}
                      {isInThreshold(
                        item?.evaporator?.activeChwSetPoint || "",
                        "evaporator.activeChwSetPoint",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.evaporator?.activeChwSetPoint || "",
                              "evaporator.activeChwSetPoint",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.evaporator?.chwInletTemperature || "",
                          "evaporator.chwInletTemperature",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.evaporator?.chwInletTemperature || ""}
                      {isInThreshold(
                        item?.evaporator?.chwInletTemperature || "",
                        "evaporator.chwInletTemperature",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.evaporator?.chwInletTemperature || "",
                              "evaporator.chwInletTemperature",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.evaporator?.chwOutletTemperature || "",
                          "evaporator.chwOutletTemperature",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.evaporator?.chwOutletTemperature || ""}
                      {isInThreshold(
                        item?.evaporator?.chwOutletTemperature || "",
                        "evaporator.chwOutletTemperature",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.evaporator?.chwOutletTemperature || "",
                              "evaporator.chwOutletTemperature",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.evaporator?.refrigerantSaturatedTempCkt01 || "",
                          "evaporator.refrigerantSaturatedTempCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.evaporator?.refrigerantSaturatedTempCkt01 || ""}
                      {isInThreshold(
                        item?.evaporator?.refrigerantSaturatedTempCkt01 || "",
                        "evaporator.refrigerantSaturatedTempCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.evaporator?.refrigerantSaturatedTempCkt01 ||
                                "",
                              "evaporator.refrigerantSaturatedTempCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.evaporator?.refrigerantSaturatedTempCkt02 || "",
                          "evaporator.refrigerantSaturatedTempCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.evaporator?.refrigerantSaturatedTempCkt02 || ""}
                      {isInThreshold(
                        item?.evaporator?.refrigerantSaturatedTempCkt02 || "",
                        "evaporator.refrigerantSaturatedTempCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.evaporator?.refrigerantSaturatedTempCkt02 ||
                                "",
                              "evaporator.refrigerantSaturatedTempCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.evaporator?.refrigerantPressureCkt01 || "",
                          "evaporator.refrigerantPressureCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.evaporator?.refrigerantPressureCkt01 || ""}
                      {isInThreshold(
                        item?.evaporator?.refrigerantPressureCkt01 || "",
                        "evaporator.refrigerantPressureCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.evaporator?.refrigerantPressureCkt01 || "",
                              "evaporator.refrigerantPressureCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.evaporator?.refrigerantPressureCkt02 || "",
                          "evaporator.refrigerantPressureCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.evaporator?.refrigerantPressureCkt02 || ""}
                      {isInThreshold(
                        item?.evaporator?.refrigerantPressureCkt02 || "",
                        "evaporator.refrigerantPressureCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.evaporator?.refrigerantPressureCkt02 || "",
                              "evaporator.refrigerantPressureCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.evaporator?.approachCkt01 || "",
                          "evaporator.approachCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.evaporator?.approachCkt01 || ""}
                      {isInThreshold(
                        item?.evaporator?.approachCkt01 || "",
                        "evaporator.approachCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.evaporator?.approachCkt01 || "",
                              "evaporator.approachCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.evaporator?.approachCkt02 || "",
                          "evaporator.approachCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.evaporator?.approachCkt02 || ""}
                      {isInThreshold(
                        item?.evaporator?.approachCkt02 || "",
                        "evaporator.approachCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.evaporator?.approachCkt02 || "",
                              "evaporator.approachCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.condenser?.inletPressure || "",
                          "condenser.inletPressure",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.condenser?.inletPressure || ""}
                      {isInThreshold(
                        item?.condenser?.inletPressure || "",
                        "condenser.inletPressure",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.condenser?.inletPressure || "",
                              "condenser.inletPressure",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.condenser?.outletPressure || "",
                          "condenser.outletPressure",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.condenser?.outletPressure || ""}
                      {isInThreshold(
                        item?.condenser?.outletPressure || "",
                        "condenser.outletPressure",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.condenser?.outletPressure || "",
                              "condenser.outletPressure",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.condenser?.inletTemperature || "",
                          "condenser.inletTemperature",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.condenser?.inletTemperature || ""}
                      {isInThreshold(
                        item?.condenser?.inletTemperature || "",
                        "condenser.inletTemperature",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.condenser?.inletTemperature || "",
                              "condenser.inletTemperature",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.condenser?.outletTemperature || "",
                          "condenser.outletTemperature",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.condenser?.outletTemperature || ""}
                      {isInThreshold(
                        item?.condenser?.outletTemperature || "",
                        "condenser.outletTemperature",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.condenser?.outletTemperature || "",
                              "condenser.outletTemperature",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.condenser?.refrigerantSaturatedTempCkt01 || "",
                          "condenser.refrigerantSaturatedTempCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.condenser?.refrigerantSaturatedTempCkt01 || ""}
                      {isInThreshold(
                        item?.condenser?.refrigerantSaturatedTempCkt01 || "",
                        "condenser.refrigerantSaturatedTempCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.condenser?.refrigerantSaturatedTempCkt01 ||
                                "",
                              "condenser.refrigerantSaturatedTempCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.condenser?.refrigerantSaturatedTempCkt02 || "",
                          "condenser.refrigerantSaturatedTempCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.condenser?.refrigerantSaturatedTempCkt02 || ""}
                      {isInThreshold(
                        item?.condenser?.refrigerantSaturatedTempCkt02 || "",
                        "condenser.refrigerantSaturatedTempCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.condenser?.refrigerantSaturatedTempCkt02 ||
                                "",
                              "condenser.refrigerantSaturatedTempCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.condenser?.refrigerantPressureCkt01 || "",
                          "condenser.refrigerantPressureCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.condenser?.refrigerantPressureCkt01 || ""}
                      {isInThreshold(
                        item?.condenser?.refrigerantPressureCkt01 || "",
                        "condenser.refrigerantPressureCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.condenser?.refrigerantPressureCkt01 || "",
                              "condenser.refrigerantPressureCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.condenser?.refrigerantPressureCkt02 || "",
                          "condenser.refrigerantPressureCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.condenser?.refrigerantPressureCkt02 || ""}
                      {isInThreshold(
                        item?.condenser?.refrigerantPressureCkt02 || "",
                        "condenser.refrigerantPressureCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.condenser?.refrigerantPressureCkt02 || "",
                              "condenser.refrigerantPressureCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.condenser?.approachCkt01 || "",
                          "condenser.approachCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.condenser?.approachCkt01 || ""}
                      {isInThreshold(
                        item?.condenser?.approachCkt01 || "",
                        "condenser.approachCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.condenser?.approachCkt01 || "",
                              "condenser.approachCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.condenser?.approachCkt02 || "",
                          "condenser.approachCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.condenser?.approachCkt02 || ""}
                      {isInThreshold(
                        item?.condenser?.approachCkt02 || "",
                        "condenser.approachCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.condenser?.approachCkt02 || "",
                              "condenser.approachCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td>
                      {item?.compressor?.startsCkt01
                        ? moment(item?.compressor?.startsCkt01).format(
                            "HH:mm DD-MM-YYYY"
                          )
                        : ""}
                    </td>
                    <td>
                      {item?.compressor?.startsCkt02
                        ? moment(item?.compressor?.startsCkt02).format(
                            "HH:mm DD-MM-YYYY"
                          )
                        : ""}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.compressor?.runningTimeCkt01 || "",
                          "compressor.runningTimeCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.compressor?.runningTimeCkt01 || ""}
                      {isInThreshold(
                        item?.compressor?.runningTimeCkt01 || "",
                        "compressor.runningTimeCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.compressor?.runningTimeCkt01 || "",
                              "compressor.runningTimeCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.compressor?.runningTimeCkt02 || "",
                          "compressor.runningTimeCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.compressor?.runningTimeCkt02 || ""}
                      {isInThreshold(
                        item?.compressor?.runningTimeCkt02 || "",
                        "compressor.runningTimeCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.compressor?.runningTimeCkt02 || "",
                              "compressor.runningTimeCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.compressor?.oilTankPressureCkt01 || "",
                          "compressor.oilTankPressureCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.compressor?.oilTankPressureCkt01 || ""}
                      {isInThreshold(
                        item?.compressor?.oilTankPressureCkt01 || "",
                        "compressor.oilTankPressureCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.compressor?.oilTankPressureCkt01 || "",
                              "compressor.oilTankPressureCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.compressor?.oilTankPressureCkt02 || "",
                          "compressor.oilTankPressureCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.compressor?.oilTankPressureCkt02 || ""}
                      {isInThreshold(
                        item?.compressor?.oilTankPressureCkt02 || "",
                        "compressor.oilTankPressureCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.compressor?.oilTankPressureCkt02 || "",
                              "compressor.oilTankPressureCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.compressor?.oilDischargePressureCkt01 || "",
                          "compressor.oilDischargePressureCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.compressor?.oilDischargePressureCkt01 || ""}
                      {isInThreshold(
                        item?.compressor?.oilDischargePressureCkt01 || "",
                        "compressor.oilDischargePressureCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.compressor?.oilDischargePressureCkt01 || "",
                              "compressor.oilDischargePressureCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.compressor?.oilDischargePressureCkt02 || "",
                          "compressor.oilDischargePressureCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.compressor?.oilDischargePressureCkt02 || ""}
                      {isInThreshold(
                        item?.compressor?.oilDischargePressureCkt02 || "",
                        "compressor.oilDischargePressureCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.compressor?.oilDischargePressureCkt02 || "",
                              "compressor.oilDischargePressureCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.compressor?.oilDiffOilPressureCkt01 || "",
                          "compressor.oilDiffOilPressureCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.compressor?.oilDiffOilPressureCkt01 || ""}
                      {isInThreshold(
                        item?.compressor?.oilDiffOilPressureCkt01 || "",
                        "compressor.oilDiffOilPressureCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.compressor?.oilDiffOilPressureCkt01 || "",
                              "compressor.oilDiffOilPressureCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.compressor?.oilDiffOilPressureCkt02 || "",
                          "compressor.oilDiffOilPressureCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.compressor?.oilDiffOilPressureCkt02 || ""}
                      {isInThreshold(
                        item?.compressor?.oilDiffOilPressureCkt02 || "",
                        "compressor.oilDiffOilPressureCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.compressor?.oilDiffOilPressureCkt02 || "",
                              "compressor.oilDiffOilPressureCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.compressor?.oilTankTempCkt01 || "",
                          "compressor.oilTankTempCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.compressor?.oilTankTempCkt01 || ""}
                      {isInThreshold(
                        item?.compressor?.oilTankTempCkt01 || "",
                        "compressor.oilTankTempCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.compressor?.oilTankTempCkt01 || "",
                              "compressor.oilTankTempCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.compressor?.oilTankTempCkt02 || "",
                          "compressor.oilTankTempCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.compressor?.oilTankTempCkt02 || ""}
                      {isInThreshold(
                        item?.compressor?.oilTankTempCkt02 || "",
                        "compressor.oilTankTempCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.compressor?.oilTankTempCkt02 || "",
                              "compressor.oilTankTempCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.compressor?.igvPositionCkt01 || "",
                          "compressor.igvPositionCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.compressor?.igvPositionCkt01 || ""}
                      {isInThreshold(
                        item?.compressor?.igvPositionCkt01 || "",
                        "compressor.igvPositionCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.compressor?.igvPositionCkt01 || "",
                              "compressor.igvPositionCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.compressor?.igvPositionCkt02 || "",
                          "compressor.igvPositionCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.compressor?.igvPositionCkt02 || ""}
                      {isInThreshold(
                        item?.compressor?.igvPositionCkt02 || "",
                        "compressor.igvPositionCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.compressor?.igvPositionCkt02 || "",
                              "compressor.igvPositionCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.compressor?.igvStepsCkt01 || "",
                          "compressor.igvStepsCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.compressor?.igvStepsCkt01 || ""}
                      {isInThreshold(
                        item?.compressor?.igvStepsCkt01 || "",
                        "compressor.igvStepsCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.compressor?.igvStepsCkt01 || "",
                              "compressor.igvStepsCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.compressor?.igvStepsCkt02 || "",
                          "compressor.igvStepsCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.compressor?.igvStepsCkt02 || ""}
                      {isInThreshold(
                        item?.compressor?.igvStepsCkt02 || "",
                        "compressor.igvStepsCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.compressor?.igvStepsCkt02 || "",
                              "compressor.igvStepsCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motor?.rlaL1Ckt01 || "",
                          "motor.rlaL1Ckt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motor?.rlaL1Ckt01 || ""}
                      {isInThreshold(
                        item?.motor?.rlaL1Ckt01 || "",
                        "motor.rlaL1Ckt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motor?.rlaL1Ckt01 || "",
                              "motor.rlaL1Ckt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motor?.rlaL1Ckt02 || "",
                          "motor.rlaL1Ckt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motor?.rlaL1Ckt02 || ""}
                      {isInThreshold(
                        item?.motor?.rlaL1Ckt02 || "",
                        "motor.rlaL1Ckt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motor?.rlaL1Ckt02 || "",
                              "motor.rlaL1Ckt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motor?.rlaL2Ckt01 || "",
                          "motor.rlaL2Ckt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motor?.rlaL2Ckt01 || ""}
                      {isInThreshold(
                        item?.motor?.rlaL2Ckt01 || "",
                        "motor.rlaL2Ckt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motor?.rlaL2Ckt01 || "",
                              "motor.rlaL2Ckt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motor?.rlaL2Ckt02 || "",
                          "motor.rlaL2Ckt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motor?.rlaL2Ckt02 || ""}
                      {isInThreshold(
                        item?.motor?.rlaL2Ckt02 || "",
                        "motor.rlaL2Ckt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motor?.rlaL2Ckt02 || "",
                              "motor.rlaL2Ckt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motor?.rlaL3Ckt01 || "",
                          "motor.rlaL3Ckt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motor?.rlaL3Ckt01 || ""}
                      {isInThreshold(
                        item?.motor?.rlaL3Ckt01 || "",
                        "motor.rlaL3Ckt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motor?.rlaL3Ckt01 || "",
                              "motor.rlaL3Ckt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motor?.rlaL3Ckt02 || "",
                          "motor.rlaL3Ckt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motor?.rlaL3Ckt02 || ""}
                      {isInThreshold(
                        item?.motor?.rlaL3Ckt02 || "",
                        "motor.rlaL3Ckt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motor?.rlaL3Ckt02 || "",
                              "motor.rlaL3Ckt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motor?.compLineCurrentL1Ckt01 || "",
                          "motor.compLineCurrentL1Ckt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motor?.compLineCurrentL1Ckt01 || ""}
                      {isInThreshold(
                        item?.motor?.compLineCurrentL1Ckt01 || "",
                        "motor.compLineCurrentL1Ckt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motor?.compLineCurrentL1Ckt01 || "",
                              "motor.compLineCurrentL1Ckt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motor?.compLineCurrentL1Ckt02 || "",
                          "motor.compLineCurrentL1Ckt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motor?.compLineCurrentL1Ckt02 || ""}
                      {isInThreshold(
                        item?.motor?.compLineCurrentL1Ckt02 || "",
                        "motor.compLineCurrentL1Ckt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motor?.compLineCurrentL1Ckt02 || "",
                              "motor.compLineCurrentL1Ckt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motor?.compLineCurrentL2Ckt01 || "",
                          "motor.compLineCurrentL2Ckt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motor?.compLineCurrentL2Ckt01 || ""}
                      {isInThreshold(
                        item?.motor?.compLineCurrentL2Ckt01 || "",
                        "motor.compLineCurrentL2Ckt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motor?.compLineCurrentL2Ckt01 || "",
                              "motor.compLineCurrentL2Ckt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motor?.compLineCurrentL2Ckt02 || "",
                          "motor.compLineCurrentL2Ckt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motor?.compLineCurrentL2Ckt02 || ""}
                      {isInThreshold(
                        item?.motor?.compLineCurrentL2Ckt02 || "",
                        "motor.compLineCurrentL2Ckt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motor?.compLineCurrentL2Ckt02 || "",
                              "motor.compLineCurrentL2Ckt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motor?.compLineCurrentL3Ckt01 || "",
                          "motor.compLineCurrentL3Ckt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motor?.compLineCurrentL3Ckt01 || ""}
                      {isInThreshold(
                        item?.motor?.compLineCurrentL3Ckt01 || "",
                        "motor.compLineCurrentL3Ckt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motor?.compLineCurrentL3Ckt01 || "",
                              "motor.compLineCurrentL3Ckt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motor?.compLineCurrentL3Ckt02 || "",
                          "motor.compLineCurrentL3Ckt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motor?.compLineCurrentL3Ckt02 || ""}
                      {isInThreshold(
                        item?.motor?.compLineCurrentL3Ckt02 || "",
                        "motor.compLineCurrentL3Ckt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motor?.compLineCurrentL3Ckt02 || "",
                              "motor.compLineCurrentL3Ckt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motor?.compLineVoltABCkt01 || "",
                          "motor.compLineVoltABCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motor?.compLineVoltABCkt01 || ""}
                      {isInThreshold(
                        item?.motor?.compLineVoltABCkt01 || "",
                        "motor.compLineVoltABCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motor?.compLineVoltABCkt01 || "",
                              "motor.compLineVoltABCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motor?.compLineVoltABCkt02 || "",
                          "motor.compLineVoltABCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motor?.compLineVoltABCkt02 || ""}
                      {isInThreshold(
                        item?.motor?.compLineVoltABCkt02 || "",
                        "motor.compLineVoltABCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motor?.compLineVoltABCkt02 || "",
                              "motor.compLineVoltABCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motor?.compLineVoltBCCkt01 || "",
                          "motor.compLineVoltBCCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motor?.compLineVoltBCCkt01 || ""}
                      {isInThreshold(
                        item?.motor?.compLineVoltBCCkt01 || "",
                        "motor.compLineVoltBCCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motor?.compLineVoltBCCkt01 || "",
                              "motor.compLineVoltBCCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motor?.compLineVoltBCCkt02 || "",
                          "motor.compLineVoltBCCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motor?.compLineVoltBCCkt02 || ""}
                      {isInThreshold(
                        item?.motor?.compLineVoltBCCkt02 || "",
                        "motor.compLineVoltBCCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motor?.compLineVoltBCCkt02 || "",
                              "motor.compLineVoltBCCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motor?.compLineVoltACCkt01 || "",
                          "motor.compLineVoltACCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motor?.compLineVoltACCkt01 || ""}
                      {isInThreshold(
                        item?.motor?.compLineVoltACCkt01 || "",
                        "motor.compLineVoltACCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motor?.compLineVoltACCkt01 || "",
                              "motor.compLineVoltACCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motor?.compLineVoltACCkt02 || "",
                          "motor.compLineVoltACCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motor?.compLineVoltACCkt02 || ""}
                      {isInThreshold(
                        item?.motor?.compLineVoltACCkt02 || "",
                        "motor.compLineVoltACCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motor?.compLineVoltACCkt02 || "",
                              "motor.compLineVoltACCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motor?.compressorKilowattsCkt01 || "",
                          "motor.compressorKilowattsCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motor?.compressorKilowattsCkt01 || ""}
                      {isInThreshold(
                        item?.motor?.compressorKilowattsCkt01 || "",
                        "motor.compressorKilowattsCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motor?.compressorKilowattsCkt01 || "",
                              "motor.compressorKilowattsCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motor?.compressorKilowattsCkt02 || "",
                          "motor.compressorKilowattsCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motor?.compressorKilowattsCkt02 || ""}
                      {isInThreshold(
                        item?.motor?.compressorKilowattsCkt02 || "",
                        "motor.compressorKilowattsCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motor?.compressorKilowattsCkt02 || "",
                              "motor.compressorKilowattsCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motor?.compressorPowerFactorCkt01 || "",
                          "motor.compressorPowerFactorCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motor?.compressorPowerFactorCkt01 || ""}
                      {isInThreshold(
                        item?.motor?.compressorPowerFactorCkt01 || "",
                        "motor.compressorPowerFactorCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motor?.compressorPowerFactorCkt01 || "",
                              "motor.compressorPowerFactorCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motor?.compressorPowerFactorCkt02 || "",
                          "motor.compressorPowerFactorCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motor?.compressorPowerFactorCkt02 || ""}
                      {isInThreshold(
                        item?.motor?.compressorPowerFactorCkt02 || "",
                        "motor.compressorPowerFactorCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motor?.compressorPowerFactorCkt02 || "",
                              "motor.compressorPowerFactorCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motor?.compWindingTempW1Ckt01 || "",
                          "motor.compWindingTempW1Ckt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motor?.compWindingTempW1Ckt01 || ""}
                      {isInThreshold(
                        item?.motor?.compWindingTempW1Ckt01 || "",
                        "motor.compWindingTempW1Ckt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motor?.compWindingTempW1Ckt01 || "",
                              "motor.compWindingTempW1Ckt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motor?.compWindingTempW1Ckt02 || "",
                          "motor.compWindingTempW1Ckt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motor?.compWindingTempW1Ckt02 || ""}
                      {isInThreshold(
                        item?.motor?.compWindingTempW1Ckt02 || "",
                        "motor.compWindingTempW1Ckt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motor?.compWindingTempW1Ckt02 || "",
                              "motor.compWindingTempW1Ckt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motor?.compWindingTempW2Ckt01 || "",
                          "motor.compWindingTempW2Ckt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motor?.compWindingTempW2Ckt01 || ""}
                      {isInThreshold(
                        item?.motor?.compWindingTempW2Ckt01 || "",
                        "motor.compWindingTempW2Ckt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motor?.compWindingTempW2Ckt01 || "",
                              "motor.compWindingTempW2Ckt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motor?.compWindingTempW2Ckt02 || "",
                          "motor.compWindingTempW2Ckt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motor?.compWindingTempW2Ckt02 || ""}
                      {isInThreshold(
                        item?.motor?.compWindingTempW2Ckt02 || "",
                        "motor.compWindingTempW2Ckt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motor?.compWindingTempW2Ckt02 || "",
                              "motor.compWindingTempW2Ckt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motor?.compWindingTempW3Ckt01 || "",
                          "motor.compWindingTempW3Ckt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motor?.compWindingTempW3Ckt01 || ""}
                      {isInThreshold(
                        item?.motor?.compWindingTempW3Ckt01 || "",
                        "motor.compWindingTempW3Ckt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motor?.compWindingTempW3Ckt01 || "",
                              "motor.compWindingTempW3Ckt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.motor?.compWindingTempW3Ckt02 || "",
                          "motor.compWindingTempW3Ckt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.motor?.compWindingTempW3Ckt02 || ""}
                      {isInThreshold(
                        item?.motor?.compWindingTempW3Ckt02 || "",
                        "motor.compWindingTempW3Ckt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.motor?.compWindingTempW3Ckt02 || "",
                              "motor.compWindingTempW3Ckt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td>
                      {item?.purge?.isPurgeModeAdaptiveCkt01 !== undefined
                        ? item.purge?.isPurgeModeAdaptiveCkt01 === true
                          ? "Yes"
                          : "No"
                        : ""}
                    </td>
                    <td>
                      {item?.purge?.isPurgeModeAdaptiveCkt02 !== undefined
                        ? item.purge?.isPurgeModeAdaptiveCkt02 === true
                          ? "Yes"
                          : "No"
                        : ""}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.purge?.timeUntilNextPurgeCkt01 || "",
                          "purge.timeUntilNextPurgeCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.purge?.timeUntilNextPurgeCkt01 || ""}
                      {isInThreshold(
                        item?.purge?.timeUntilNextPurgeCkt01 || "",
                        "purge.timeUntilNextPurgeCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.purge?.timeUntilNextPurgeCkt01 || "",
                              "purge.timeUntilNextPurgeCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.purge?.timeUntilNextPurgeCkt02 || "",
                          "purge.timeUntilNextPurgeCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.purge?.timeUntilNextPurgeCkt02 || ""}
                      {isInThreshold(
                        item?.purge?.timeUntilNextPurgeCkt02 || "",
                        "purge.timeUntilNextPurgeCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.purge?.timeUntilNextPurgeCkt02 || "",
                              "purge.timeUntilNextPurgeCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.purge?.dailyPumpOutCkt01 || "",
                          "purge.dailyPumpOutCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.purge?.dailyPumpOutCkt01 || ""}
                      {isInThreshold(
                        item?.purge?.dailyPumpOutCkt01 || "",
                        "purge.dailyPumpOutCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.purge?.dailyPumpOutCkt01 || "",
                              "purge.dailyPumpOutCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.purge?.dailyPumpOutCkt02 || "",
                          "purge.dailyPumpOutCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.purge?.dailyPumpOutCkt02 || ""}
                      {isInThreshold(
                        item?.purge?.dailyPumpOutCkt02 || "",
                        "purge.dailyPumpOutCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.purge?.dailyPumpOutCkt02 || "",
                              "purge.dailyPumpOutCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.purge?.avgPumpOutCkt01 || "",
                          "purge.avgPumpOutCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.purge?.avgPumpOutCkt01 || ""}
                      {isInThreshold(
                        item?.purge?.avgPumpOutCkt01 || "",
                        "purge.avgPumpOutCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.purge?.avgPumpOutCkt01 || "",
                              "purge.avgPumpOutCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.purge?.avgPumpOutCkt02 || "",
                          "purge.avgPumpOutCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.purge?.avgPumpOutCkt02 || ""}
                      {isInThreshold(
                        item?.purge?.avgPumpOutCkt02 || "",
                        "purge.avgPumpOutCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.purge?.avgPumpOutCkt02 || "",
                              "purge.avgPumpOutCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.purge?.dailyPumpOutLimitCkt01 || "",
                          "purge.dailyPumpOutLimitCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.purge?.dailyPumpOutLimitCkt01 || ""}
                      {isInThreshold(
                        item?.purge?.dailyPumpOutLimitCkt01 || "",
                        "purge.dailyPumpOutLimitCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.purge?.dailyPumpOutLimitCkt01 || "",
                              "purge.dailyPumpOutLimitCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.purge?.dailyPumpOutLimitCkt02 || "",
                          "purge.dailyPumpOutLimitCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.purge?.dailyPumpOutLimitCkt02 || ""}
                      {isInThreshold(
                        item?.purge?.dailyPumpOutLimitCkt02 || "",
                        "purge.dailyPumpOutLimitCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.purge?.dailyPumpOutLimitCkt02 || "",
                              "purge.dailyPumpOutLimitCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.purge?.chillerOn7DaysCkt01 || "",
                          "purge.chillerOn7DaysCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.purge?.chillerOn7DaysCkt01 || ""}
                      {isInThreshold(
                        item?.purge?.chillerOn7DaysCkt01 || "",
                        "purge.chillerOn7DaysCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.purge?.chillerOn7DaysCkt01 || "",
                              "purge.chillerOn7DaysCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.purge?.chillerOn7DaysCkt02 || "",
                          "purge.chillerOn7DaysCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.purge?.chillerOn7DaysCkt02 || ""}
                      {isInThreshold(
                        item?.purge?.chillerOn7DaysCkt02 || "",
                        "purge.chillerOn7DaysCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.purge?.chillerOn7DaysCkt02 || "",
                              "purge.chillerOn7DaysCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.purge?.pumpOutChillerOn7DaysCkt01 || "",
                          "purge.pumpOutChillerOn7DaysCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.purge?.pumpOutChillerOn7DaysCkt01 || ""}
                      {isInThreshold(
                        item?.purge?.pumpOutChillerOn7DaysCkt01 || "",
                        "purge.pumpOutChillerOn7DaysCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.purge?.pumpOutChillerOn7DaysCkt01 || "",
                              "purge.pumpOutChillerOn7DaysCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.purge?.pumpOutChillerOn7DaysCkt02 || "",
                          "purge.pumpOutChillerOn7DaysCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.purge?.pumpOutChillerOn7DaysCkt02 || ""}
                      {isInThreshold(
                        item?.purge?.pumpOutChillerOn7DaysCkt02 || "",
                        "purge.pumpOutChillerOn7DaysCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.purge?.pumpOutChillerOn7DaysCkt02 || "",
                              "purge.pumpOutChillerOn7DaysCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.purge?.pumpOutChillerOff7DaysCkt01 || "",
                          "purge.pumpOutChillerOff7DaysCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.purge?.pumpOutChillerOff7DaysCkt01 || ""}
                      {isInThreshold(
                        item?.purge?.pumpOutChillerOff7DaysCkt01 || "",
                        "purge.pumpOutChillerOff7DaysCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.purge?.pumpOutChillerOff7DaysCkt01 || "",
                              "purge.pumpOutChillerOff7DaysCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.purge?.pumpOutChillerOff7DaysCkt02 || "",
                          "purge.pumpOutChillerOff7DaysCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.purge?.pumpOutChillerOff7DaysCkt02 || ""}
                      {isInThreshold(
                        item?.purge?.pumpOutChillerOff7DaysCkt02 || "",
                        "purge.pumpOutChillerOff7DaysCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.purge?.pumpOutChillerOff7DaysCkt02 || "",
                              "purge.pumpOutChillerOff7DaysCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.purge?.pumpOutLifeCkt01 || "",
                          "purge.pumpOutLifeCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.purge?.pumpOutLifeCkt01 || ""}
                      {isInThreshold(
                        item?.purge?.pumpOutLifeCkt01 || "",
                        "purge.pumpOutLifeCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.purge?.pumpOutLifeCkt01 || "",
                              "purge.pumpOutLifeCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.purge?.pumpOutLifeCkt02 || "",
                          "purge.pumpOutLifeCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.purge?.pumpOutLifeCkt02 || ""}
                      {isInThreshold(
                        item?.purge?.pumpOutLifeCkt02 || "",
                        "purge.pumpOutLifeCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.purge?.pumpOutLifeCkt02 || "",
                              "purge.pumpOutLifeCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.purge?.purgeRefCompSuctionTempCkt01 || "",
                          "purge.purgeRefCompSuctionTempCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.purge?.purgeRefCompSuctionTempCkt01 || ""}
                      {isInThreshold(
                        item?.purge?.purgeRefCompSuctionTempCkt01 || "",
                        "purge.purgeRefCompSuctionTempCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.purge?.purgeRefCompSuctionTempCkt01 || "",
                              "purge.purgeRefCompSuctionTempCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.purge?.purgeRefCompSuctionTempCkt02 || "",
                          "purge.purgeRefCompSuctionTempCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.purge?.purgeRefCompSuctionTempCkt02 || ""}
                      {isInThreshold(
                        item?.purge?.purgeRefCompSuctionTempCkt02 || "",
                        "purge.purgeRefCompSuctionTempCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.purge?.purgeRefCompSuctionTempCkt02 || "",
                              "purge.purgeRefCompSuctionTempCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.purge?.purgeLiquidTempCkt01 || "",
                          "purge.purgeLiquidTempCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.purge?.purgeLiquidTempCkt01 || ""}
                      {isInThreshold(
                        item?.purge?.purgeLiquidTempCkt01 || "",
                        "purge.purgeLiquidTempCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.purge?.purgeLiquidTempCkt01 || "",
                              "purge.purgeLiquidTempCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.purge?.purgeLiquidTempCkt02 || "",
                          "purge.purgeLiquidTempCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.purge?.purgeLiquidTempCkt02 || ""}
                      {isInThreshold(
                        item?.purge?.purgeLiquidTempCkt02 || "",
                        "purge.purgeLiquidTempCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.purge?.purgeLiquidTempCkt02 || "",
                              "purge.purgeLiquidTempCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.purge?.carbonTankTempCkt01 || "",
                          "purge.carbonTankTempCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.purge?.carbonTankTempCkt01 || ""}
                      {isInThreshold(
                        item?.purge?.carbonTankTempCkt01 || "",
                        "purge.carbonTankTempCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.purge?.carbonTankTempCkt01 || "",
                              "purge.carbonTankTempCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.purge?.carbonTankTempCkt02 || "",
                          "purge.carbonTankTempCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.purge?.carbonTankTempCkt02 || ""}
                      {isInThreshold(
                        item?.purge?.carbonTankTempCkt02 || "",
                        "purge.carbonTankTempCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.purge?.carbonTankTempCkt02 || "",
                              "purge.carbonTankTempCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.purge?.oilTankLevelCkt01 || "",
                          "purge.oilTankLevelCkt01",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.purge?.oilTankLevelCkt01 || ""}
                      {isInThreshold(
                        item?.purge?.oilTankLevelCkt01 || "",
                        "purge.oilTankLevelCkt01",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.purge?.oilTankLevelCkt01 || "",
                              "purge.oilTankLevelCkt01",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td
                      className={
                        isInThreshold(
                          item?.purge?.oilTankLevelCkt02 || "",
                          "purge.oilTankLevelCkt02",
                          threshold
                        )
                          ? "text-danger"
                          : ""
                      }
                    >
                      {item?.purge?.oilTankLevelCkt02 || ""}
                      {isInThreshold(
                        item?.purge?.oilTankLevelCkt02 || "",
                        "purge.oilTankLevelCkt02",
                        threshold
                      ) && (
                        <ShowThresholdIcon
                          isLessThan={
                            isValueLessThanThreshold(
                              item?.purge?.oilTankLevelCkt02 || "",
                              "purge.oilTankLevelCkt02",
                              threshold
                            ) as boolean
                          }
                        />
                      )}
                    </td>
                    <td>{item?.recordedBy || ""}</td>
                    <td>
                      {item && item.status ? UPS_STATUS[item.status] : ""}
                    </td>
                    <td>{item?.remarks || ""}</td>
                    <td>
                      {item?.date
                        ? moment(item.date).format(READ_AT_TIME_FORMAT)
                        : ""}
                    </td>
                    <td align="center" className="no-wrap">
                      {item && item.status === "submitted" && (
                        <span
                          onClick={(ele) =>
                            handleCallOutOpenClose(true, ele, item)
                          }
                        >
                          <Icon
                            path={mdiDotsVertical}
                            size={0.9}
                            className="pointer"
                            color={BRAND_SECONDARY}
                          />
                        </span>
                        // <React.Fragment>
                        //   <TooltipHost
                        //     content="Update"
                        //     calloutProps={{ gapSpace: 0 }}
                        //   >
                        //     <span
                        //       onClick={() =>
                        //         handleDialogOpen(true, item?._id as string)
                        //       }
                        //     >
                        //       <Icon
                        //         path={mdiPencil}
                        //         size={0.7}
                        //         className="pointer"
                        //       />
                        //     </span>
                        //   </TooltipHost>
                        //   &nbsp;&nbsp;
                        //   <TooltipHost
                        //     content="Accept"
                        //     calloutProps={{ gapSpace: 0 }}
                        //   >
                        //     <span
                        //       onClick={() =>
                        //         handleAcceptOrReject(item?._id as string, true)
                        //       }
                        //     >
                        //       <Icon
                        //         path={mdiCheck}
                        //         size={0.8}
                        //         className="pointer"
                        //         color="green"
                        //       />
                        //     </span>
                        //   </TooltipHost>
                        //   &nbsp;&nbsp;
                        //   <TooltipHost
                        //     content="Reject"
                        //     calloutProps={{ gapSpace: 0 }}
                        //   >
                        //     <span
                        //       onClick={() =>
                        //         handleRejectOpenDialog(
                        //           item?._id as string,
                        //           item?.remarks
                        //         )
                        //       }
                        //     >
                        //       <Icon
                        //         path={mdiClose}
                        //         size={0.8}
                        //         className="pointer"
                        //         color="red"
                        //       />
                        //     </span>
                        //   </TooltipHost>
                        // </React.Fragment>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Stack>
      {state.manageActionsCallout.isOpen && (
        <Callout
          styles={{ root: { width: 95, padding: 20 } }}
          role="alertdialog"
          ariaLabelledBy={
            state.manageActionsCallout && state.manageActionsCallout.data?._id
          }
          gapSpace={0}
          target={state.manageActionsCallout.element}
          directionalHint={DirectionalHint.leftCenter}
          onDismiss={() => handleCallOutOpenClose(false)}
          // setInitialFocus={true}
        >
          <Stack tokens={childrenGap8}>
            <Stack.Item className="pointer">
              <span
                onClick={() =>
                  handleDialogOpen(
                    true,
                    state.manageActionsCallout.data?._id as string
                  )
                }
                className="callout-options"
              >
                Edit
              </span>
            </Stack.Item>
            <Stack.Item className="pointer">
              <span
                onClick={() =>
                  handleAcceptOrReject(
                    state.manageActionsCallout.data?._id as string,
                    true
                  )
                }
                className="callout-options"
              >
                Accept
              </span>
            </Stack.Item>
            <Stack.Item className="pointer">
              <span
                onClick={() =>
                  handleRejectOpenDialog(
                    state.manageActionsCallout.data?._id as string,
                    state.manageActionsCallout.data?.remarks
                  )
                }
                className="callout-options"
              >
                Reject
              </span>
            </Stack.Item>
          </Stack>
        </Callout>
      )}
      {state.manageWccDialog.isOpen && (
        <ManageWccDialog
          data={state.manageWccDialog.data}
          handleLoading={handleLoading}
          handleDialogClose={(data: IWaterCooledChillerReading) =>
            handleDialogClose(data)
          }
          threshold={threshold}
        />
      )}
      {state.manageRejectDialog.isOpen && (
        <ManageAllRejectDialog
          handleRejectCloseDialog={(isSuccess: boolean, remarks?: string) =>
            handleRejectCloseDialog(isSuccess, remarks)
          }
          remarks={state.manageRejectDialog.remarks}
          subText="water cooled chiller reading"
        />
      )}
    </React.Fragment>
  );
};

export default WccTable;
